import { inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map, of, switchMap } from 'rxjs';
import { UserFacade } from '@personal-area/global/users/services';
import { PopupData, VerificationModalComponent } from '../components/verification-modal/verification-modal.component';
import { IDVerificationStatus } from '../models/verification-profile-response';
import { IdentityVerificationFacade } from '../state/facades/identity';
import { SummaryVerificationFacade } from '../state/facades/summary';

const routes = {
  banned: '/profile/verification/identity/banned',
  auto: '/profile/verification/identity/auto',
  manual: '/profile/verification/identity/manual',
  rejected: '/profile/verification/identity/us-rejected',
};

export const IdentityGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  { url }: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const { loggedUser } = inject(UserFacade);
  const identityVerificationFacade = inject(IdentityVerificationFacade);
  const summaryFacade = inject(SummaryVerificationFacade);

  if (!loggedUser.isVerified && !loggedUser.isEmailConfirmed) {
    inject(MatDialog).open(VerificationModalComponent, <MatDialogConfig<PopupData>>{
      disableClose: true,
      panelClass: 'regular-popup-container',
      data: {
        type: 'email',
      },
    });

    return router.createUrlTree(['profile']);
  }

  if (url === routes.rejected) {
    return true;
  }

  return summaryFacade.summary$.pipe(
    switchMap(({ idStatus }) => {
      switch (idStatus) {
        case IDVerificationStatus.accepted:
        case IDVerificationStatus.pending:
          return of(router.createUrlTree(['/dashboard']));
        case IDVerificationStatus.banned:
          return of(url === routes.banned || router.createUrlTree([routes.banned]));
        default:
          identityVerificationFacade.getFlow();
          return identityVerificationFacade.flow$.pipe(
            map(({ type }) => type === 'auto'
              ? url === routes.auto || router.createUrlTree([routes.auto])
              : url === routes.manual || router.createUrlTree([routes.manual]),
            ),
          );
      }
    }),
  );
};
